// ByEase Custom Cart Section JS
// Spec: docs/superpowers/specs/2026-04-10-byease-cart-phase2-design.md
//
// Architecture:
//   - Optimistic UI: input.value updates instantly on every interaction
//   - Per-line debounced commit: rapid clicks coalesce into ONE fetch
//     (the latest target value) after 350ms of quiet
//   - Server-truth resync: every commit response (success OR error)
//     rerenders the section from server-side HTML
//   - Stale-result discard: a sequence number per line ignores results
//     from older commits if the user has clicked again in the meantime
//   - Pending optimistic restore across rerender: prevents the visible
//     value from flashing back to server-truth between rapid clicks

(function () {
  'use strict';

  const SECTION_ID = 'cart-custom';
  const ROOT_ID = 'cart-custom-root';
  const DEBOUNCE_MS = 350;

  // ─── State ─────────────────────────────────────────────────────
  // lineKey -> { targetQty, timer }   debounce queue for upcoming commit
  const pending = new Map();
  // lineKey -> latest target qty waiting to be sent to server
  const latestTarget = new Map();
  // lineKey -> Promise that resolves when current commit chain finishes
  const lineLocks = new Map();

  // ─── Toast ─────────────────────────────────────────────────────
  // Renders an inline error banner as a SIBLING of #cart-custom-root
  // (not inside it) so it survives section rerenders. Inline placement
  // avoids stacking-context fights with the theme's sticky header,
  // announcement bar, and Shopify preview bar.
  function toast(msg) {
    console.warn('[cart-custom]', msg);
    const root = document.getElementById(ROOT_ID);
    if (!root || !root.parentNode) return;
    const parent = root.parentNode;
    let el = document.getElementById('cart-custom-toast');
    if (!el || el.parentNode !== parent) {
      if (el && el.parentNode) el.parentNode.removeChild(el);
      el = document.createElement('div');
      el.id = 'cart-custom-toast';
      el.className = 'cart-custom__toast';
      el.setAttribute('role', 'alert');
      parent.insertBefore(el, root);
    }
    el.textContent = msg;
    el.classList.add('cart-custom__toast--visible');
    clearTimeout(el._hideTimer);
    el._hideTimer = setTimeout(() => {
      el.classList.remove('cart-custom__toast--visible');
    }, 4500);
  }

  // ─── Server interactions ──────────────────────────────────────
  async function postChange(lineKey, qty) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: qty,
        sections: SECTION_ID,
        sections_url: '/cart',
      }),
    });
    const body = await r.json().catch(() => ({}));
    return { ok: r.ok, status: r.status, body };
  }

  async function fetchSectionHtml() {
    try {
      const r = await fetch(`/cart?sections=${SECTION_ID}`, { cache: 'no-store' });
      if (!r.ok) return null;
      const data = await r.json();
      return (data && data[SECTION_ID]) || null;
    } catch (_) {
      return null;
    }
  }

  // ─── Rerender ─────────────────────────────────────────────────
  function applySectionHtml(html) {
    if (!html) return false;
    const newRoot = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById(ROOT_ID);
    const cur = document.getElementById(ROOT_ID);
    if (!newRoot || !cur) {
      console.warn('[cart-custom] root element missing in re-render');
      return false;
    }
    cur.replaceWith(newRoot);
    // Restore optimistic values for any lines the user is still mid-clicking,
    // then recompute totals so the displayed prices match the restored qty.
    if (pending.size > 0) {
      pending.forEach((entry, lineKey) => {
        const inp = document.querySelector(
          `.cart-custom__stepper[data-line-key="${lineKey}"] .cart-custom__stepper-input`
        );
        if (inp) inp.value = String(entry.targetQty);
      });
      recalcOptimisticTotals();
    }
    bindHandlers();
    return true;
  }

  async function syncFromServer() {
    const html = await fetchSectionHtml();
    return applySectionHtml(html);
  }

  // ─── Commit ───────────────────────────────────────────────────
  // commitChange serializes per line: at most ONE /cart/change.js fetch in
  // flight per line key. Subsequent calls update `latestTarget` and the
  // current commit-chain loop will pick up the new target after the
  // current request finishes. UI is rendered ONLY when no newer target
  // is waiting (avoids mid-flight flicker).
  async function commitChange(lineKey, qty) {
    latestTarget.set(lineKey, qty);

    // Already running for this line? The running loop will pick up our target.
    if (lineLocks.has(lineKey)) return lineLocks.get(lineKey);

    let release;
    const lock = new Promise((r) => { release = r; });
    lineLocks.set(lineKey, lock);

    (async () => {
      try {
        while (latestTarget.has(lineKey)) {
          const target = latestTarget.get(lineKey);
          latestTarget.delete(lineKey);

          let result;
          try {
            result = await postChange(lineKey, target);
          } catch (_) {
            toast('連線不穩定，請稍後再試');
            await syncFromServer();
            return;
          }

          const sectionHtml =
            result.body && result.body.sections && result.body.sections[SECTION_ID];
          const isLatest = !latestTarget.has(lineKey);

          if (!result.ok) {
            const msg =
              (result.body && (result.body.description || result.body.message)) ||
              '更新購物車失敗';
            toast(msg);
            // Always sync UI to truth on error
            if (sectionHtml) applySectionHtml(sectionHtml);
            else await syncFromServer();
            // After an error we drop any further queued targets — the user
            // can re-click if they want to keep going.
            latestTarget.delete(lineKey);
            return;
          }

          // Success — only render if this was the LATEST commit; otherwise
          // the next loop iteration will render the final state.
          if (isLatest) {
            if (sectionHtml) applySectionHtml(sectionHtml);
            else await syncFromServer();
          }
        }
      } finally {
        lineLocks.delete(lineKey);
        release();
      }
    })();

    return lock;
  }

  function scheduleCommit(lineKey, qty) {
    const existing = pending.get(lineKey);
    if (existing) clearTimeout(existing.timer);
    const entry = { targetQty: qty, timer: null };
    entry.timer = setTimeout(() => {
      pending.delete(lineKey);
      commitChange(lineKey, entry.targetQty);
    }, DEBOUNCE_MS);
    pending.set(lineKey, entry);
  }

  function cancelPending(lineKey) {
    const p = pending.get(lineKey);
    if (p) {
      clearTimeout(p.timer);
      pending.delete(lineKey);
    }
  }

  // ─── Optimistic price recalc ──────────────────────────────────
  // After any stepper / input change we recompute line totals and the
  // cart subtotal client-side from data-unit-price (in cents) and the
  // current input.value, so the user sees the totals update immediately
  // instead of waiting for the server roundtrip.
  function formatMoney(cents) {
    // ByEase is TWD-only (0 decimal places). Format as $1,234
    const value = Math.round(cents / 100);
    return '$' + value.toLocaleString('en-US');
  }

  function recalcOptimisticTotals() {
    let cartSubtotal = 0;
    let totalCount = 0;
    document.querySelectorAll('.cart-custom__item').forEach((item) => {
      const unitPrice = parseInt(item.dataset.unitPrice, 10) || 0;
      const inp = item.querySelector('.cart-custom__stepper-input');
      const qty = inp ? Math.max(0, parseInt(inp.value, 10) || 0) : 0;
      const lineTotal = unitPrice * qty;
      cartSubtotal += lineTotal;
      totalCount += qty;
      const totalEl = item.querySelector('[data-line-total]');
      if (totalEl) totalEl.textContent = formatMoney(lineTotal);
    });
    const subtotalEl = document.querySelector('[data-cart-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cartSubtotal);
    const grandEl = document.querySelector('[data-cart-grand]');
    if (grandEl) grandEl.textContent = formatMoney(cartSubtotal) + ' TWD';
    const countEl = document.querySelector('[data-cart-count]');
    if (countEl) countEl.textContent = String(totalCount);
  }

  // ─── Helpers ──────────────────────────────────────────────────
  function getStepperContext(el) {
    const stepper = el.closest('.cart-custom__stepper');
    if (!stepper) return null;
    const input = stepper.querySelector('.cart-custom__stepper-input');
    if (!input) return null;
    const lineKey = stepper.dataset.lineKey;
    if (!lineKey) return null;
    const qty = parseInt(input.value, 10);
    const item = stepper.closest('.cart-custom__item');
    return {
      stepper,
      input,
      lineKey,
      item,
      qty: isNaN(qty) ? 0 : Math.max(0, qty),
      maxQty: getMaxQtyForItem(item),
    };
  }

  // Returns the stock limit for a line (null = no limit tracked)
  function getMaxQtyForItem(item) {
    if (!item) return null;
    const raw = item.dataset.maxQty;
    if (!raw || raw === '') return null;
    const n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  }

  // ─── Handlers ─────────────────────────────────────────────────
  function onPlusClick(e) {
    const ctx = getStepperContext(e.currentTarget);
    if (!ctx) return;
    // Block at stock ceiling — don't even send the request
    if (ctx.maxQty !== null && ctx.qty >= ctx.maxQty) {
      toast(`此商品庫存僅剩 ${ctx.maxQty} 件`);
      return;
    }
    const next = ctx.qty + 1;
    ctx.input.value = String(next); // optimistic
    recalcOptimisticTotals();
    scheduleCommit(ctx.lineKey, next);
  }

  function onMinusClick(e) {
    const ctx = getStepperContext(e.currentTarget);
    if (!ctx) return;
    if (ctx.qty <= 1) {
      // − at 1 → remove the line. Don't show 0 in the input visually.
      cancelPending(ctx.lineKey);
      commitChange(ctx.lineKey, 0);
      return;
    }
    const next = ctx.qty - 1;
    ctx.input.value = String(next);
    recalcOptimisticTotals();
    scheduleCommit(ctx.lineKey, next);
  }

  // Real-time strip non-numeric characters as user types
  function onInputType(e) {
    const input = e.currentTarget;
    const cleaned = input.value.replace(/[^0-9]/g, '');
    if (cleaned !== input.value) input.value = cleaned;
  }

  // Commit on blur / Enter
  function onInputCommit(e) {
    const input = e.currentTarget;
    const stepper = input.closest('.cart-custom__stepper');
    if (!stepper) return;
    const lineKey = stepper.dataset.lineKey;
    const item = stepper.closest('.cart-custom__item');
    const maxQty = getMaxQtyForItem(item);
    let qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) qty = 1;
    if (qty === 0) {
      cancelPending(lineKey);
      commitChange(lineKey, 0);
      return;
    }
    // Clamp to stock ceiling if tracked
    if (maxQty !== null && qty > maxQty) {
      qty = maxQty;
      toast(`此商品庫存僅剩 ${maxQty} 件`);
    }
    input.value = String(qty);
    recalcOptimisticTotals();
    scheduleCommit(lineKey, qty);
  }

  function onRemoveClick(e) {
    const lineKey = e.currentTarget.dataset.lineKey;
    if (!lineKey) return;
    cancelPending(lineKey);
    commitChange(lineKey, 0);
  }

  // ─── Order note ──────────────────────────────────────────────
  let noteTimer = null;
  function onNoteInput(e) {
    const value = e.currentTarget.value;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(async () => {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ note: value }),
        });
      } catch (_) {
        toast('備註儲存失敗');
      }
    }, DEBOUNCE_MS);
  }

  // ─── Bind ─────────────────────────────────────────────────────
  function bindHandlers() {
    document.querySelectorAll('.cart-custom__stepper-btn[data-action="increase"]').forEach((b) => {
      b.removeEventListener('click', onPlusClick);
      b.addEventListener('click', onPlusClick);
    });
    document.querySelectorAll('.cart-custom__stepper-btn[data-action="decrease"]').forEach((b) => {
      b.removeEventListener('click', onMinusClick);
      b.addEventListener('click', onMinusClick);
    });
    document.querySelectorAll('.cart-custom__stepper-input').forEach((i) => {
      i.removeEventListener('input', onInputType);
      i.addEventListener('input', onInputType);
      i.removeEventListener('change', onInputCommit);
      i.addEventListener('change', onInputCommit);
    });
    document.querySelectorAll('.cart-custom__item-remove').forEach((b) => {
      b.removeEventListener('click', onRemoveClick);
      b.addEventListener('click', onRemoveClick);
    });
    const note = document.getElementById('cart-custom-note');
    if (note) {
      note.removeEventListener('input', onNoteInput);
      note.addEventListener('input', onNoteInput);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindHandlers);
  } else {
    bindHandlers();
  }

  console.log('[cart-custom] script loaded (v4)');
})();
